import { useState, useEffect, useLayoutEffect, useRef, useSyncExternalStore } from "react";
import {
  AlarmClockOff,
  CalendarCheck,
  Cake,
  CalendarClock,
  CalendarX2,
  CheckCircle2,
  CircleSlash,
  ClipboardCopy,
  MessageCircle,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Send,
  User,
  UserPlus,
  UserX,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  bookAppointment,
  fetchAppointmentConfirmationDraft,
  setAdminAppointmentConfirmation,
} from "@/lib/api/admin";
import { copyTextToClipboard } from "@/lib/clipboard/text-clipboard";
import { publishCalendarRefresh } from "@/lib/realtime/appointment-realtime-bridge";
import { hasPermission } from "@/lib/stores/permissions-store";
import { searchPatients } from "@/lib/api/users";
import { ensurePatientThread } from "@/lib/api/threads";
import { requestOpenThreadMessage } from "@/lib/notifications/notification-navigation";
import {
  getAppointmentCheckinVersion,
  getCheckinForAppointment,
  hasCheckinCoverage,
  subscribeAppointmentCheckins,
} from "@/lib/stores/appointment-checkin-store";
import {
  LOCK_15M_MINUTES,
  NIL_PATIENT_ID,
  birthdayAgeOnDate,
  buildEventColorTheme,
  effectiveLock15m,
  formatTime,
  isAppointmentPartiallyFree,
  isBirthdayOnDate,
  isCancelledCalendarStatus,
  isNewPatient,
  newPatientPhoneFromEvent,
} from "./calendar-utils";
import {
  MIN_APPOINTMENT_MINUTES,
  canMutateAppointmentEvent,
  closestTimeGeometryElement,
  isRecurringOccurrenceId,
  measureColumnGeometry,
  snapMinutes,
} from "./calendar-drag-geometry";
import { useCalendarMutations } from "./calendar-mutations";
import { SelectedDayEventDetails } from "./day-view/selected-day-event-details";
import { SlotFormSheet } from "./day-view/slot-form-sheet";
import { AppointmentFormSheet } from "./day-view/appointment-form-sheet";
import { DateTime } from "luxon";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { maskPhoneInput, phoneDigitCount, validatePhoneInput } from "@/lib/phone";
import { isDirectEntryEvent } from "@/lib/calendars";

function SlotBookingModal({ event, open, onOpenChange, use12HourFormat, onEditSlot }) {
  const [patientSearchTerm, setPatientSearchTerm] = useState("");
  const [patientsList, setPatientsList] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [slotReason, setSlotReason] = useState("");

  // "New patient" quick-create mode: book the slot with just a name + phone.
  // No patient record is created — the appointment itself carries both.
  const [newPatientMode, setNewPatientMode] = useState(false);
  const [newPatientName, setNewPatientName] = useState("");
  // Empty, not prefilled with "+65": a prefill concatenates with a pasted number that
  // already carries the country code, producing an id no WhatsApp account answers to.
  const [newPatientPhone, setNewPatientPhone] = useState("");

  const [isBooking, setIsBooking] = useState(false);
  const [bookingError, setBookingError] = useState("");

  const isSlot = event.kind === "slot";

  useEffect(() => {
    if (!isSlot || !open) return;

    if (!patientSearchTerm || patientSearchTerm.trim().length < 2) {
      setPatientsList([]);
      setIsSearching(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await searchPatients({ searchTerm: patientSearchTerm });
        setPatientsList(res.items || []);
      } catch (err) {
        console.error("Failed to search patients:", err);
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [patientSearchTerm, isSlot, open]);

  useEffect(() => {
    if (open) {
      setPatientSearchTerm("");
      setPatientsList([]);
      setSelectedPatient(null);
      setSlotReason("");
      setBookingError("");
      setNewPatientMode(false);
      setNewPatientName("");
      setNewPatientPhone("");
    }
  }, [open]);

  const newPatientPhoneCheck = validatePhoneInput(newPatientPhone);
  // The phone is optional here — an empty field books without one. A non-empty field must
  // be a real number: a malformed one creates a thread that accepts messages and delivers
  // none, which is indistinguishable from a working conversation until someone notices.
  const newPatientPhoneValid = newPatientPhone.trim() === "" || newPatientPhoneCheck.ok;
  const newPatientPhoneHint =
    !newPatientPhoneValid && phoneDigitCount(newPatientPhone) >= 8 ? newPatientPhoneCheck.message : "";
  const canBookNewPatient =
    newPatientMode && newPatientName.trim().length > 0 && newPatientPhoneValid;
  const mutations = useCalendarMutations();

  const handleBookSlot = async () => {
    if (!selectedPatient && !canBookNewPatient) return;
    // RBAC: booking a slot creates an appointment — calendar:manage (server enforces too).
    if (!hasPermission("calendar", "manage")) {
      setBookingError("View only — your access level doesn't allow booking appointments.");
      return;
    }
    setIsBooking(true);
    setBookingError("");

    try {
      const durationMinutes = Math.round(event.endAt.diff(event.startAt, "minutes").minutes);
      const dtStart = DateTime.fromJSDate(new Date(event.startAt), { zone: event.timezone });

      // New-patient mode books against the zero-id sentinel: no user record is
      // created — the name becomes the appointment title and the phone lives on
      // the appointment itself (like unlinked Plato snapshots), so it links up
      // with a patient created later under a matching number.
      const patientId = String(selectedPatient?.id || "").trim() || NIL_PATIENT_ID;

      const slotCalendarId = String(event.calendarId || "").trim();
      const body = JSON.stringify({
        doctorId: String(event.doctorId || ""),
        doctorName: String(event.doctor || "Unknown Doctor"),
        date: dtStart.toFormat("yyyy-MM-dd"),
        time: dtStart.toFormat("HH:mm"),
        durationMinutes: durationMinutes,
        description: slotReason,
        // The slot's own calendar: required to book slots of a non-blocking calendar
        // (the server can't infer it from the doctor), harmless for the rest.
        ...(slotCalendarId ? { calendarId: slotCalendarId } : {}),
        ...(selectedPatient
          ? {}
          : {
              title: newPatientName.trim(),
              // Only send a number that validates — an unusable one would create a
              // WhatsApp thread that silently swallows every reminder.
              phone: newPatientPhoneCheck.ok ? newPatientPhone : "",
            }),
      });

      const response = await bookAppointment({
        normalizedUserId: patientId,
        body
      });

      if (!response || !response.success) {
        throw new Error("Failed to book the slot");
      }

      setSlotReason("");
      setBookingError("");
      onOpenChange(false);
      // Offer to send a confirmation for the freshly booked appointment. A real
      // patient messages by their linked user; a new-patient quick-create messages
      // by the phone just entered (backend find-or-creates a patient by number),
      // so only offer it when that phone is a real number.
      const createdId = String(response?.appointment?.id || "").trim();
      if (createdId && selectedPatient) {
        mutations?.requestAppointmentConfirmation?.({
          appointmentId: createdId,
          patientUserId: String(selectedPatient.id || "").trim(),
          patientName:
            String(
              selectedPatient.displayName ||
                `${selectedPatient.firstName || ""} ${selectedPatient.lastName || ""}`.trim(),
            ).trim() || undefined,
          phone: String(selectedPatient.phone || "").trim(),
        });
      } else if (createdId && newPatientPhoneCheck.ok) {
        mutations?.requestAppointmentConfirmation?.({
          appointmentId: createdId,
          patientUserId: NIL_PATIENT_ID,
          patientName: newPatientName.trim() || undefined,
          phone: newPatientPhone.trim(),
        });
      }
    } catch (err) {
      setBookingError(err instanceof Error ? err.message : "Failed to book the slot");
    } finally {
      setIsBooking(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="pr-10">
          <DialogTitle>Book slot</DialogTitle>
          <DialogDescription>
            Choose a patient and reason to book this slot.
          </DialogDescription>
          {typeof onEditSlot === "function" ? (
            <div className="pt-2">
              <button
                type="button"
                onClick={() => {
                  onEditSlot(event);
                  onOpenChange(false);
                }}
                title="Open the parent slot definition for editing"
                className="inline-flex h-7 items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Pencil className="size-3" aria-hidden />
                Edit slot definition
              </button>
            </div>
          ) : null}
        </DialogHeader>

        <div className="py-4 space-y-4">
          <section className="space-y-3 rounded-lg border border-border bg-background p-3 shadow-sm sm:p-4">
            <SelectedDayEventDetails event={event} use12HourFormat={use12HourFormat} />
          </section>

          {isSlot && (
            <div className="space-y-4 mt-4 pt-4 border-t border-border">
              {!selectedPatient && newPatientMode ? (
                <div className="space-y-3 animate-in fade-in zoom-in-95 duration-200">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium inline-flex items-center gap-2">
                      <UserPlus className="h-4 w-4 text-violet-500" />
                      New patient
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs text-muted-foreground"
                      onClick={() => setNewPatientMode(false)}
                    >
                      Back to search
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Input
                      placeholder="Patient name (becomes the appointment title)"
                      value={newPatientName}
                      onChange={(e) => setNewPatientName(e.target.value)}
                      autoFocus
                    />
                    <Input
                      type="tel"
                      inputMode="tel"
                      placeholder="+6591234567"
                      className="font-mono text-sm"
                      value={newPatientPhone}
                      onChange={(e) => setNewPatientPhone(maskPhoneInput(e.target.value))}
                    />
                    {newPatientPhoneHint ? (
                      <p className="text-[11px] font-medium text-destructive">{newPatientPhoneHint}</p>
                    ) : null}
                    <Input
                      placeholder="Reason for visit, notes for the doctor..."
                      value={slotReason}
                      onChange={(e) => setSlotReason(e.target.value)}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Books the slot without a patient record — the appointment carries the name and
                    phone, and links up automatically once a patient with this number exists.
                  </p>
                </div>
              ) : !selectedPatient ? (
                <div className="space-y-3">
                  <div className="space-y-2 relative">
                    <label className="text-sm font-medium">Patient</label>
                    <div className="relative mt-1">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search patient by name..."
                        className="pl-9"
                        value={patientSearchTerm}
                        onChange={(e) => setPatientSearchTerm(e.target.value)}
                      />
                      {isSearching && (
                        <div className="absolute right-2.5 top-2.5">
                          <span className="h-4 w-4 block rounded-full border-2 border-primary border-t-transparent animate-spin" />
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewPatientMode(true)}
                    className="flex w-full items-center gap-2 rounded-md border border-dashed border-violet-400/60 px-3 py-2 text-sm font-medium text-violet-600 transition-colors hover:bg-violet-500/10 dark:border-violet-400/40 dark:text-violet-300"
                  >
                    <UserPlus className="h-4 w-4 shrink-0" />
                    New patient — book with just a name &amp; phone
                  </button>
                  {patientsList.length > 0 && (
                    <ScrollArea className="h-40 rounded-md border bg-muted/30">
                      <div className="p-1.5 space-y-1">
                        {patientsList.map(p => {
                          const displayName = typeof p.displayName === "string" ? p.displayName : `${p.firstName || ""} ${p.lastName || ""}`.trim() || p.id;
                          return (
                            <button
                              key={p.id}
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted font-medium transition-colors cursor-pointer"
                              onClick={() => {
                                setSelectedPatient(p);
                                setPatientsList([]);
                                setPatientSearchTerm("");
                              }}
                            >
                              {displayName}
                            </button>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                  {patientSearchTerm.trim().length >= 2 && !isSearching && patientsList.length === 0 && (
                    <p className="text-xs text-muted-foreground">No patients found.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-4 animate-in fade-in zoom-in-95 duration-200">
                  <div className="flex flex-col bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 p-3 border border-emerald-500/20 rounded-md gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold flex items-center gap-2">
                        <User className="h-4 w-4 shrink-0" />
                        <span className="truncate">
                          {typeof selectedPatient.displayName === "string"
                            ? selectedPatient.displayName
                            : `${selectedPatient.firstName || ""} ${selectedPatient.lastName || ""}`.trim() || "Selected Patient"}
                        </span>
                      </span>
                      <Button variant="ghost" size="sm" className="h-6 shrink-0 text-xs text-emerald-700 hover:text-emerald-800 hover:bg-emerald-500/20 dark:text-emerald-400 dark:hover:text-emerald-300 dark:hover:bg-emerald-500/20" onClick={() => setSelectedPatient(null)}>Change</Button>
                    </div>

                    <div className="grid grid-cols-2 gap-y-1 gap-x-2 text-xs opacity-90 mt-1">
                      {selectedPatient.email && (
                        <div className="truncate"><span className="font-medium opacity-70">Email:</span> {selectedPatient.email}</div>
                      )}
                      {selectedPatient.phone && (
                        <div className="truncate"><span className="font-medium opacity-70">Phone:</span> {selectedPatient.phone}</div>
                      )}
                      {selectedPatient.profile?.DOB && (
                        <div className="truncate"><span className="font-medium opacity-70">DOB:</span> {formatDate(selectedPatient.profile.DOB)}</div>
                      )}
                      {selectedPatient.profile?.Sex && (
                        <div className="truncate"><span className="font-medium opacity-70">Sex:</span> {selectedPatient.profile.Sex}</div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Description</label>
                    <Input
                      placeholder="Reason for visit, notes for the doctor..."
                      value={slotReason}
                      onChange={(e) => setSlotReason(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {bookingError && (
            <p className="text-sm font-medium text-destructive mt-2">{bookingError}</p>
          )}
        </div>

        <DialogFooter>
          <div className={`flex w-full ${selectedPatient || newPatientMode ? "sm:justify-end justify-between" : "justify-end"} items-center gap-2`}>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isBooking}>Cancel</Button>
            {(selectedPatient || newPatientMode) && (
              <Button
                onClick={handleBookSlot}
                disabled={isBooking || (!selectedPatient && !canBookNewPatient)}
                aria-busy={isBooking}
              >
                {isBooking ? "Booking..." : "Book Slot"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Diagonal hatch so cancelled appointments read as voided without hiding text. */
function CancelledStripeOverlay() {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-1 rounded-[inherit] bg-[repeating-linear-gradient(315deg,transparent,transparent_6px,rgb(0_0_0/0.11)_6px,rgb(0_0_0/0.11)_7px)] dark:bg-[repeating-linear-gradient(315deg,transparent,transparent_6px,rgb(255_255_255/0.16)_6px,rgb(255_255_255/0.16)_7px)]"
      aria-hidden
    />
  );
}

// Start/end labels for an event's time range. In 12-hour format, the start time drops its
// "AM"/"PM" suffix when it matches the end time's meridiem (e.g. "9:30" / "10:00 AM"
// instead of "9:30 AM" / "10:00 AM") — repeating it on both is redundant and, on narrow
// event cards (e.g. overlapping appointments split into side-by-side columns), the extra
// characters push out the patient name/subtitle.
function formatEventTimeRange(startAt, endAt, use12HourFormat) {
  if (!use12HourFormat) {
    return { startLabel: startAt.toFormat("HH:mm"), endLabel: endAt.toFormat("HH:mm") };
  }
  const sameMeridiem = startAt.toFormat("a") === endAt.toFormat("a");
  return {
    startLabel: sameMeridiem ? startAt.toFormat("h:mm") : startAt.toFormat("h:mm a"),
    endLabel: endAt.toFormat("h:mm a"),
  };
}

function resolveEventPatient(event) {
  // Patient name when one is attached; otherwise the item's own title so
  // patient-less entries (blockers, plain events) keep their label.
  return (
    String(event?.patient || event?.displayName || "").trim() ||
    String(event?.title || "").trim()
  );
}

/** Week view: patient, doctor, location only (no types/description; no inline times). */
function buildCompactEventLabels(event, isSlot) {
  const patient = resolveEventPatient(event);
  const doctor = String(event.doctor || "").trim();
  const location = String(event.location || "").trim();
  // Standalone-calendar entries have neither doctor nor location; name the calendar
  // instead so the card still says where it belongs.
  const calendarName = String(event.calendarName || "").trim();
  const context = [doctor, location].filter(Boolean).join(" · ") || calendarName;

  if (patient) {
    return {
      primary: patient,
      secondary: context,
    };
  }
  if (doctor) {
    return {
      primary: doctor,
      secondary: location,
    };
  }
  return {
    primary: isSlot ? "Open" : "Appointment",
    secondary: context,
  };
}

/** Dashed avatar for appointments with no linked patient record yet. */
function NewPatientAvatar({ variant = "full", usesColorHex, themeText }) {
  return (
    <div
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-full border-2 border-dashed sm:size-8",
        variant === "compact" && "size-6 sm:size-7",
        usesColorHex
          ? cn(
              "border-(--event-dot-light) bg-(--event-bg-ghost-light) dark:border-(--event-dot-dark) dark:bg-(--event-bg-ghost-dark)",
              themeText,
            )
          : "border-violet-400/75 bg-violet-100/90 text-violet-700 shadow-[inset_0_0_0_1px_rgb(139_92_246/0.12)] dark:border-violet-400/55 dark:bg-violet-950/55 dark:text-violet-200",
      )}
      title="New patient"
      aria-hidden
    >
      <UserPlus
        className={cn(
          "size-3 sm:size-3.5",
          variant === "compact" && "size-2.5 sm:size-3",
        )}
        strokeWidth={2}
      />
    </div>
  );
}

function NewPatientBadge({ usesColorHex, className }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-sm bg-violet-500/15 px-1 py-px text-[0.5625rem] font-semibold uppercase tracking-wide text-violet-700 dark:bg-violet-400/15 dark:text-violet-200",
        usesColorHex &&
          "bg-(--event-bg-ghost-light) text-(--event-text-light) dark:bg-(--event-bg-ghost-dark) dark:text-(--event-text-dark)",
        className,
      )}
    >
      New
    </span>
  );
}

/** Pink cake next to the patient name when the visit lands on their birthday. */
function BirthdayCakeBadge({ age, variant = "full" }) {
  return (
    <span
      className="inline-flex shrink-0 items-center text-pink-500 drop-shadow-[0_1px_1px_rgb(236_72_153/0.35)] dark:text-pink-300"
      title={age != null ? `Birthday today — turns ${age}! 🎉` : "Birthday today! 🎉"}
      role="img"
      aria-label="Patient has a birthday today"
    >
      <Cake
        className={cn("size-3.5 sm:size-4", variant === "compact" && "size-3 sm:size-3.5")}
        strokeWidth={2.25}
      />
    </span>
  );
}

// Minutes before the visit at which a not-yet-checked-in patient turns the
// card's indicator red.
const CHECKIN_WARNING_MINUTES = 15;

/**
 * Arrived / not-arrived cue next to the patient name. `state.kind`:
 * "arrived" (green check — patient checked in at the clinic, in consult, or
 * done) or "late" (amber pulsing alarm — not checked in with less than
 * CHECKIN_WARNING_MINUTES to the visit). A "late" state with `noShow` set is a
 * no-show: it renders a distinct static rose UserX (no pulsing dot), so a settled
 * no-show reads apart from an imminent-but-not-yet-arrived patient. `noShow` covers
 * both an explicit no_show lifecycle and a `derived` one (the visit window elapsed
 * with no check-in) — `derived` only softens the tooltip wording.
 */
function CheckinBadge({ state, variant = "full" }) {
  if (!state) return null;
  const sizeClass = cn("size-3.5 sm:size-4", variant === "compact" && "size-3 sm:size-3.5");
  if (state.kind === "arrived") {
    const title = state.done
      ? "Visit completed"
      : state.inConsult
        ? "In consultation"
        : `Checked in${state.atLabel ? ` at ${state.atLabel}` : ""}`;
    return (
      <span
        className="inline-flex shrink-0 items-center text-emerald-600 drop-shadow-[0_1px_1px_rgb(16_185_129/0.35)] dark:text-emerald-300"
        title={title}
        role="img"
        aria-label="Patient has checked in"
      >
        <CheckCircle2 className={sizeClass} strokeWidth={2.25} />
      </span>
    );
  }
  if (state.noShow) {
    return (
      <span
        className="inline-flex shrink-0 items-center text-rose-600 dark:text-rose-400"
        title={
          state.derived
            ? "No-show — the visit ended without a check-in"
            : "Marked as no-show"
        }
        role="img"
        aria-label="Patient did not show up"
      >
        <UserX className={sizeClass} strokeWidth={2.25} />
      </span>
    );
  }
  return (
    <span
      className="relative inline-flex shrink-0 items-center text-amber-600 dark:text-amber-400"
      title="Not checked in — the visit starts in less than 15 minutes"
      role="img"
      aria-label="Patient has not arrived"
    >
      <AlarmClockOff className={sizeClass} strokeWidth={2.25} />
      <span
        className="absolute -right-0.5 -top-0.5 size-1.5 animate-ping rounded-full bg-amber-500 motion-reduce:animate-none"
        aria-hidden
      />
    </span>
  );
}

/**
 * Attendance-confirmation cue next to the patient name. `status`:
 * "confirmed" (green calendar-check — patient confirmed over WhatsApp or staff
 * marked it) or "pending" (amber paper-plane — confirmation sent, awaiting the
 * patient's reply). The calendar-check glyph is deliberately distinct from
 * CheckinBadge's plain circle-check so a confirmed-but-not-yet-arrived
 * appointment never reads as "checked in".
 */
function ConfirmationBadge({ status, variant = "full" }) {
  const value = String(status || "").trim();
  if (value !== "confirmed" && value !== "pending") return null;
  const sizeClass = cn("size-3.5 sm:size-4", variant === "compact" && "size-3 sm:size-3.5");
  if (value === "confirmed") {
    return (
      <span
        className="inline-flex shrink-0 items-center text-emerald-600 drop-shadow-[0_1px_1px_rgb(16_185_129/0.35)] dark:text-emerald-300"
        title="Confirmed"
        role="img"
        aria-label="Appointment confirmed"
      >
        <CalendarCheck className={sizeClass} strokeWidth={2.25} />
      </span>
    );
  }
  return (
    <span
      className="inline-flex shrink-0 items-center text-amber-600 dark:text-amber-400"
      title="Confirmation sent — awaiting the patient's reply"
      role="img"
      aria-label="Awaiting patient confirmation"
    >
      <Send className={sizeClass} strokeWidth={2.25} />
    </span>
  );
}

/**
 * Marks the bookable remainder of a lock-first-15-min appointment: a dashed
 * divider at the 15-minute boundary with a faint hatch below, so the card
 * still reads as its full duration while signalling the free tail. `topPercent`
 * is the 15-minute boundary as a fraction of the card height.
 */
function FreeRemainderMarker({ topPercent }) {
  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 z-2 overflow-hidden rounded-b-[inherit] border-t border-dashed border-foreground/25 bg-[repeating-linear-gradient(135deg,transparent,transparent_5px,rgb(0_0_0/0.045)_5px,rgb(0_0_0/0.045)_6px)] dark:border-white/25 dark:bg-[repeating-linear-gradient(135deg,transparent,transparent_5px,rgb(255_255_255/0.05)_5px,rgb(255_255_255/0.05)_6px)]"
      style={{ top: `${topPercent}%` }}
      aria-hidden
    >
      <span className="absolute right-1 top-0.5 hidden rounded-sm bg-background/70 px-1 py-px text-[0.5rem] font-semibold uppercase leading-none tracking-wide text-muted-foreground/90 shadow-sm @[7rem]/event:inline-block">
        Open
      </span>
    </div>
  );
}

/**
 * Prominent ribbon so cancelled state is obvious at a glance. A recorded
 * cancellation reason (the bot relays the patient's WhatsApp reason; staff type
 * it in the cancel dialog) sits under the "CANCELLED" label behind a hairline
 * divider — quoted, italic, up to two lines, and always on the hover tooltip —
 * so staff see why without opening the appointment. The reason is width-gated so
 * narrow cards keep just the label.
 */
// Gap (px) left between the bottom of the CANCELLED banner and the card content
// when a cancellation reason has pushed the banner taller. Measured, not a fixed
// padding, so it stays constant however the reason wraps.
const CANCELLED_BANNER_GAP_PX = 5;

function CancelledBanner({ reason, innerRef }) {
  const trimmedReason = String(reason || "").trim();
  return (
    <div
      ref={innerRef}
      className="pointer-events-none absolute inset-x-0 top-0 z-2 rounded-t-[inherit] bg-rose-600/50 px-1.5 py-0.5 text-center shadow-sm backdrop-blur-[2px] dark:bg-rose-700/55 sm:py-1"
      role="status"
      aria-label={trimmedReason ? `Cancelled: ${trimmedReason}` : "Cancelled appointment"}
      title={trimmedReason || undefined}
    >
      <span className="block text-[0.625rem] font-bold uppercase leading-none tracking-[0.08em] text-white drop-shadow-sm sm:text-xs">
        Cancelled
      </span>
      {trimmedReason ? (
        <span className="mx-auto mt-1 hidden max-w-[96%] border-t border-white/25 px-0.5 pt-1 text-[0.625rem] font-medium italic leading-snug text-white/95 drop-shadow-sm @[7rem]/event:block">
          <span className="line-clamp-2">“{trimmedReason}”</span>
        </span>
      ) : null}
    </div>
  );
}

/**
 * Shared calendar event block used in day and week views for consistent look.
 * @param {Object} props
 * @param {Object} props.event - Event { id, title, description?, patient?, location?, startAt, endAt, color?, colorHex?, status? }
 * @param {boolean} [props.use12HourFormat=false]
 * @param {'full'|'compact'} [props.variant='full'] - full: title, description, patient, location, time; compact: title, patient, time
 * @param {'button'|'div'} [props.as='div']
 * @param {boolean} [props.isSelected]
 * @param {boolean} [props.isHighlighted] - Deep-linked from elsewhere in the app; wears an amber ring.
 * @param {(id: string) => void} [props.onClick]
 * @param {string} [props.className]
 * @param {React.CSSProperties} [props.style]
 */
function CalendarEventItem({
  event,
  use12HourFormat = false,
  variant = "full",
  as: Component = "div",
  isSelected = false,
  isHighlighted = false,
  onClick,
  className,
  style,
  timeAxis = null,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [slotEditOpen, setSlotEditOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  // Live edge-resize preview: while active it overrides the card's grid-derived
  // top/bottom with explicit px so the card visually grows/shrinks under the
  // pointer; committed on pointer-up through applyAppointmentUpdate.
  const [resizePreview, setResizePreview] = useState(null);
  const resizeSessionRef = useRef(null);
  const cardNodeRef = useRef(null);
  const mutations = useCalendarMutations();

  const isSlot = event.kind === "slot";
  const isAppointment = event.kind === "appointment";

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableNodeRef,
    isDragging,
  } = useDraggable({
    id: event.id,
    data: { type: 'appointment', event },
    // Cancelled appointments, recurring occurrences and patient-less Plato
    // snapshots stay in place — the update endpoint can't move them.
    disabled: !canMutateAppointmentEvent(event),
  });

  const {
    isOver,
    setNodeRef: setDroppableNodeRef,
  } = useDroppable({
    id: event.id,
    data: { type: 'slot', event },
    disabled: !isSlot,
  });

  const setNodeRef = (node) => {
    cardNodeRef.current = node;
    if (isAppointment) setDraggableNodeRef(node);
    if (isSlot) setDroppableNodeRef(node);
  };

  const canResize = Boolean(
    canMutateAppointmentEvent(event) && timeAxis && mutations?.applyAppointmentUpdate,
  );

  const updateResizePreview = (session, clientY) => {
    const durationMinutes = Math.round(
      session.initialEndAt.diff(session.initialStartAt, "minutes").minutes,
    );
    let deltaMinutes = snapMinutes((clientY - session.startClientY) / session.geometry.pxPerMinute);
    let nextStartAt = session.initialStartAt;
    let nextEndAt = session.initialEndAt;
    if (session.edge === "top") {
      const startDayMinutes = session.initialStartAt.hour * 60 + session.initialStartAt.minute;
      deltaMinutes = Math.min(
        Math.max(deltaMinutes, timeAxis.startMinutes - startDayMinutes),
        durationMinutes - MIN_APPOINTMENT_MINUTES,
      );
      nextStartAt = session.initialStartAt.plus({ minutes: deltaMinutes });
    } else {
      // Midnight end reads as 0 minutes into the day — treat it as 24:00.
      const endDayMinutes =
        session.initialEndAt.hour * 60 + session.initialEndAt.minute || 24 * 60;
      deltaMinutes = Math.min(
        Math.max(deltaMinutes, MIN_APPOINTMENT_MINUTES - durationMinutes),
        timeAxis.endMinutes - endDayMinutes,
      );
      nextEndAt = session.initialEndAt.plus({ minutes: deltaMinutes });
    }
    const deltaPx = deltaMinutes * session.geometry.pxPerMinute;
    const preview = {
      edge: session.edge,
      startAt: nextStartAt,
      endAt: nextEndAt,
      topPx: session.edge === "top" ? session.cardTopPx + deltaPx : session.cardTopPx,
      heightPx:
        session.edge === "top" ? session.cardHeightPx - deltaPx : session.cardHeightPx + deltaPx,
    };
    session.lastPreview = preview;
    setResizePreview(preview);
  };

  const beginResize = (edge) => (e) => {
    if (!canResize || e.button !== 0) return;
    // Keep dnd-kit's PointerSensor (spread on the card) from starting a
    // move-drag; the handle owns this gesture via pointer capture. This runs
    // BEFORE the geometry measurement can bail — otherwise a grab that fails to
    // measure falls through to the card and silently turns into a move-drag.
    e.stopPropagation();
    e.preventDefault();
    const geometry = measureColumnGeometry(
      closestTimeGeometryElement(cardNodeRef.current),
      timeAxis,
    );
    if (!geometry) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const cardRect = cardNodeRef.current.getBoundingClientRect();
    const parentRect =
      cardNodeRef.current.offsetParent?.getBoundingClientRect?.() || cardRect;
    resizeSessionRef.current = {
      edge,
      geometry,
      startClientY: e.clientY,
      initialStartAt: event.startAt,
      initialEndAt: event.endAt,
      cardTopPx: cardRect.top - parentRect.top,
      cardHeightPx: cardRect.height,
      lastPreview: null,
    };
    updateResizePreview(resizeSessionRef.current, e.clientY);
  };

  const handleResizeMove = (e) => {
    const session = resizeSessionRef.current;
    if (!session) return;
    updateResizePreview(session, e.clientY);
  };

  const abortResize = () => {
    resizeSessionRef.current = null;
    setResizePreview(null);
  };

  const finishResize = () => {
    const session = resizeSessionRef.current;
    resizeSessionRef.current = null;
    setResizePreview(null);
    if (!session?.lastPreview) return;
    const { startAt, endAt } = session.lastPreview;
    if (
      startAt.toMillis() === session.initialStartAt.toMillis() &&
      endAt.toMillis() === session.initialEndAt.toMillis()
    ) {
      return;
    }
    const minutes = Math.round(endAt.diff(startAt, "minutes").minutes);
    mutations.applyAppointmentUpdate({
      event,
      changes: { startAt, endAt },
      toastLabel: `Duration set to ${minutes} min (${formatTime(startAt, { hour12: use12HourFormat })} – ${formatTime(endAt, { hour12: use12HourFormat })})`,
    });
  };

  const isResizing = resizePreview != null;
  useEffect(() => {
    if (!isResizing) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") abortResize();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResizing]);

  // Build the theme for any colored event — slot OR appointment. Appointments often
  // inherit their color from the parent calendar's metadata.sourceColor.
  const colorTheme = buildEventColorTheme(event.colorHex);
  const usesColorHex = Boolean(colorTheme);

  // Slot: dashed border on a ghost fill — signals "open / available, click to book".
  const themeCardSlot =
    "border-(--event-border-light) bg-(--event-bg-ghost-light) hover:bg-(--event-bg-ghost-hover-light) dark:border-(--event-border-dark) dark:bg-(--event-bg-ghost-dark) dark:hover:bg-(--event-bg-ghost-hover-dark)";
  // Booking: solid border on a filled tint — signals "committed, has an owner".
  const themeCardAppt =
    "border-(--event-border-light) bg-(--event-bg-light) hover:bg-(--event-bg-hover-light) dark:border-(--event-border-dark) dark:bg-(--event-bg-dark) dark:hover:bg-(--event-bg-hover-dark)";
  const themeText =
    "text-(--event-text-light) dark:text-(--event-text-dark)";
  // Slot: dashed outline on a transparent ground — signals "open".
  const themeAvatarSlot =
    "border-2 border-dashed border-(--event-dot-light) dark:border-(--event-dot-dark)";
  // Appointment: filled saturated tone — signals "booked". Inverts text/bg vars so the
  // initial reads on a deep variant of the calendar color, mirroring the dark/light split.
  const themeAvatarAppt =
    "bg-(--event-text-light) text-(--event-bg-light) dark:bg-(--event-text-dark) dark:text-(--event-bg-dark)";

  const sharedStyle = {
    ...(usesColorHex ? colorTheme : {}),
    ...style,
    // Live resize overrides the class-based top-0.5/bottom-1 placement with
    // explicit px so the card tracks the pointer; the li keeps its grid row.
    ...(resizePreview
      ? {
          top: `${resizePreview.topPx}px`,
          height: `${resizePreview.heightPx}px`,
          bottom: "auto",
          zIndex: 60,
        }
      : {}),
    // While dragging, the DragOverlay ghost is the single moving element — the
    // source card stays in place, knocked back, so there aren't two cards
    // chasing the pointer.
    // Open slots are knocked back so booked appointments stand out; the factor is the
    // user's "Slot opacity" setting (CSS var set on the calendar wrapper). Full strength
    // while being dragged-over as a drop target (isOver). Set inline (not a class) because
    // this inline opacity would otherwise override a utility class.
    opacity: isDragging ? 0.35 : isSlot && !isOver ? "var(--calendar-slot-opacity, 0.5)" : 1,
  };

  const cancelled = isCancelledCalendarStatus(event.status);
  const cancellationReason = cancelled ? String(event.cancellationReason || "").trim() : "";
  // Every cancelled card clears its CANCELLED banner by a constant
  // CANCELLED_BANNER_GAP_PX. The banner height varies — a reason adds a divider
  // plus up to two quoted lines — so measure the actual banner rather than
  // guessing a fixed padding. This keeps the gap identical on reason and
  // no-reason cards alike.
  const cancelledBannerRef = useRef(null);
  const [cancelledBannerPad, setCancelledBannerPad] = useState(0);
  useLayoutEffect(() => {
    if (!cancelled) {
      setCancelledBannerPad(0);
      return undefined;
    }
    const el = cancelledBannerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => setCancelledBannerPad(el.offsetHeight + CANCELLED_BANNER_GAP_PX);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [cancelled, cancellationReason]);

  // Check-in indicator (today's appointments only). The store version ticks
  // every minute (poll) and on queueTenant WS pushes, so the late window is
  // re-evaluated against the current clock without any per-card timers.
  const checkinVersion = useSyncExternalStore(
    subscribeAppointmentCheckins,
    getAppointmentCheckinVersion,
    getAppointmentCheckinVersion,
  );
  let checkinState = null;
  if (isAppointment && !cancelled && checkinVersion >= 0) {
    const checkin = getCheckinForAppointment(event);
    const lifecycle = String(checkin?.lifecycle || "");
    if (lifecycle === "checked_in" || lifecycle === "done") {
      const checkedInAt = checkin?.checkedInAt
        ? DateTime.fromISO(checkin.checkedInAt).toLocal()
        : null;
      checkinState = {
        kind: "arrived",
        atLabel: checkedInAt?.isValid ? checkedInAt.toFormat("HH:mm") : "",
        inConsult: Boolean(checkin?.visitActive),
        done: lifecycle === "done",
      };
    } else if (lifecycle === "no_show") {
      checkinState = { kind: "late", noShow: true };
    } else if (hasCheckinCoverage(event)) {
      // "Late" (and the derived no-show below) is inferred from the ABSENCE of a
      // check-in, so it only applies once the store has today's data for this
      // clinic — otherwise a slow first fetch (or a clinic that doesn't run a
      // queue) would false-alarm every appointment near its start time.
      const now = DateTime.now();
      if (event.startAt?.hasSame?.(now, "day")) {
        if (event.endAt && now >= event.endAt) {
          // The visit window elapsed with no check-in on record → treat as a
          // no-show even though staff never explicitly marked it. Same UserX
          // cue as an explicit no_show; `derived` softens the tooltip wording.
          checkinState = { kind: "late", noShow: true, derived: true };
        } else if (now >= event.startAt.minus({ minutes: CHECKIN_WARNING_MINUTES })) {
          checkinState = { kind: "late" };
        }
      }
    }
  }

  const { startLabel: eventStartLabel, endLabel: eventEndLabel } = formatEventTimeRange(
    event.startAt,
    event.endAt,
    use12HourFormat,
  );
  const openRangeLabel = `${eventStartLabel}–${eventEndLabel}`;

  const isCompact = variant === "compact";

  let secondaryLine = "";
  if (!isCompact) {
    const secondaryParts = [event.doctor, event.location].filter(Boolean);
    // Doctor-less, clinic-less entries (standalone calendars) show their calendar's name.
    if (secondaryParts.length === 0 && event.calendarName) secondaryParts.push(event.calendarName);
    if (!isSlot && event.type) secondaryParts.push(event.type);
    secondaryLine = secondaryParts.slice(0, 5).join(" · ");
  }
  const instructionsText = !isSlot && variant === "full" ? event.description : "";

  const eventPatient = resolveEventPatient(event);

  const { primary: compactPrimary, secondary: compactSecondary } = isCompact
    ? buildCompactEventLabels(event, isSlot)
    : { primary: "", secondary: "" };

  const newPatient = isNewPatient(event);
  // Direct entries on standalone calendars are not patient visits: no attendance
  // confirmations (send / copy text / mark) apply to them.
  const directEntry = isAppointment && isDirectEntryEvent(event);
  // Appointment-confirmation state (metadata-derived) for the card badge + menu.
  const confirmationStatus = isAppointment ? String(event.confirmationStatus || "").trim() : "";
  const isConfirmed = confirmationStatus === "confirmed";
  // Visit lands on the patient's birthday — surface a cake so staff can wish
  // them well at the door.
  const birthdayToday = isAppointment && isBirthdayOnDate(event.patientDob, event.startAt);
  const birthdayAge = birthdayToday ? birthdayAgeOnDate(event.patientDob, event.startAt) : null;
  const newPatientTextClass = usesColorHex
    ? themeText
    : "text-violet-950 dark:text-violet-50";
  const newPatientMutedClass = usesColorHex
    ? cn(themeText, "opacity-80")
    : "text-violet-800/90 dark:text-violet-300/80";

  const initialFromName = (name) => {
    const s = String(name || "").trim();
    if (!s) return "?";
    return s[0].toUpperCase();
  };

  const body = (
    <ScrollArea className="relative z-0 h-full w-full min-w-0 rounded-[inherit]">
      <div
        className={cn(
          "flex h-full min-h-0 min-w-0 w-full items-center gap-2 overflow-hidden px-2 py-1.5 sm:gap-2.5 sm:px-2.5 sm:py-2",
          variant === "compact" && "gap-1.5 px-1.5 py-1",
          // Clear the CANCELLED banner by the measured paddingTop (inline style
          // below). These classes are only the fallback for the first frame
          // before the measure lands; a reason makes the banner taller, so its
          // fallback is larger.
          cancelled && !cancelledBannerPad && (cancellationReason ? "pt-12 sm:pt-14" : "pt-9 sm:pt-11"),
        )}
        style={
          cancelled && cancelledBannerPad ? { paddingTop: cancelledBannerPad } : undefined
        }
      >
        {isSlot && (
          // Slot avatar: dashed circle + plus glyph. Shape (circle) + icon (Plus) +
          // dashed border are three redundant cues that this is an "available, click to book" target.
          <div
            className={cn(
              "flex size-7 shrink-0 items-center justify-center rounded-full sm:size-8",
              variant === "compact" && "size-6 sm:size-7",
              usesColorHex ? cn(themeAvatarSlot, themeText) : "border-2 border-dashed border-sky-500/60 text-sky-700 dark:border-sky-400/50 dark:text-sky-300/90",
            )}
            aria-hidden
          >
            <Plus
              className={cn(
                "size-3.5 sm:size-4",
                variant === "compact" && "size-3 sm:size-3.5",
              )}
              strokeWidth={2.5}
            />
          </div>
        )}

        <div className="min-w-0 flex-1 self-start">
          {isCompact ? (
            <>
              {isSlot ? (
                <p
                  className={cn(
                    "min-w-0 truncate text-[0.6875rem] font-medium italic leading-snug sm:text-xs",
                    usesColorHex ? cn(themeText, "opacity-85") : "text-sky-900/85 dark:text-sky-200/80",
                  )}
                >
                  {compactPrimary}
                </p>
              ) : (
                <p
                  className={cn(
                    "min-w-0 truncate text-xs font-bold leading-snug sm:text-[0.8125rem]",
                    newPatient
                      ? newPatientTextClass
                      : usesColorHex
                        ? themeText
                        : "text-emerald-950 dark:text-[#f4f4ec]",
                  )}
                >
                  {newPatient || birthdayToday || checkinState || confirmationStatus ? (
                    <span className="inline-flex min-w-0 max-w-full items-center gap-1">
                      {checkinState ? <CheckinBadge state={checkinState} variant="compact" /> : null}
                      {birthdayToday ? <BirthdayCakeBadge age={birthdayAge} variant="compact" /> : null}
                      {confirmationStatus ? <ConfirmationBadge status={confirmationStatus} variant="compact" /> : null}
                      <span className="truncate">{compactPrimary}</span>
                      {newPatient ? <NewPatientBadge usesColorHex={usesColorHex} /> : null}
                    </span>
                  ) : (
                    compactPrimary
                  )}
                </p>
              )}
              <p
                className={cn(
                  "mt-0.5 min-w-0 truncate text-[0.625rem] leading-snug sm:text-[0.6875rem]",
                  usesColorHex
                    ? cn(themeText, "opacity-80")
                    : isSlot
                      ? "text-sky-800/85 dark:text-slate-400"
                      : newPatient
                        ? newPatientMutedClass
                        : "text-emerald-900/85 dark:text-emerald-300/75",
                )}
                title={compactSecondary || undefined}
              >
                <span className="font-medium tabular-nums">{openRangeLabel}</span>
                {compactSecondary ? ` · ${compactSecondary}` : null}
              </p>
            </>
          ) : (
            <>
              {isSlot ? (
                <p
                  className={cn(
                    "min-w-0 truncate text-[0.6875rem] italic leading-snug sm:text-xs",
                    usesColorHex ? cn(themeText, "opacity-85") : "text-sky-900/85 dark:text-sky-200/80",
                  )}
                >
                  <span className="font-medium">Open</span>
                  <span
                    className={cn(
                      "select-none",
                      usesColorHex ? "opacity-40" : "text-sky-400 dark:text-sky-300/40",
                    )}
                    aria-hidden
                  >
                    {" "}
                    ·{" "}
                  </span>
                  <span className={usesColorHex ? "opacity-90" : "text-sky-800/90 dark:text-sky-100/70"}>
                    {openRangeLabel}
                  </span>
                </p>
              ) : (
                <p
                  className={cn(
                    "min-w-0 truncate text-[0.8125rem] font-bold leading-snug sm:text-sm",
                    newPatient
                      ? newPatientTextClass
                      : usesColorHex
                        ? themeText
                        : "text-emerald-950 dark:text-[#f4f4ec]",
                  )}
                >
                  {newPatient || birthdayToday || checkinState || confirmationStatus ? (
                    <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
                      {checkinState ? <CheckinBadge state={checkinState} /> : null}
                      {birthdayToday ? <BirthdayCakeBadge age={birthdayAge} /> : null}
                      {confirmationStatus ? <ConfirmationBadge status={confirmationStatus} /> : null}
                      <span className="truncate">{eventPatient || event.doctor || "Appointment"}</span>
                      {newPatient ? <NewPatientBadge usesColorHex={usesColorHex} /> : null}
                    </span>
                  ) : (
                    eventPatient || event.doctor || "Appointment"
                  )}
                </p>
              )}
              {secondaryLine ? (
                <p
                  className={cn(
                    "mt-0.5 min-w-0 truncate text-[0.625rem] leading-snug sm:text-[0.6875rem]",
                    usesColorHex
                      ? cn(themeText, "opacity-80")
                      : isSlot
                        ? "text-sky-800/85 dark:text-slate-400"
                        : newPatient
                          ? newPatientMutedClass
                          : "text-emerald-900/85 dark:text-emerald-300/75",
                  )}
                  title={secondaryLine}
                >
                  {secondaryLine}
                </p>
              ) : null}
              {instructionsText ? (
                <p
                  className={cn(
                    "mt-2 min-w-0 whitespace-pre-line text-[0.625rem] leading-snug sm:text-[0.6875rem]",
                    usesColorHex
                      ? cn(themeText, "opacity-80")
                      : newPatient
                        ? newPatientMutedClass
                        : "text-emerald-900/85 dark:text-emerald-300/75",
                  )}
                >
                  <span className="font-bold">Instructions:</span>
                  {"\n"}
                  {instructionsText}
                </p>
              ) : null}
            </>
          )}
        </div>

        {!isCompact ? (
          <div
            className={cn(
              // Below ~7rem of card width (overlapping appointments splitting a day-view
              // column into narrow lanes), drop the time block entirely rather than let it
              // crowd out the patient name — the vertical position already conveys the time.
              "hidden shrink-0 self-start text-right tabular-nums @[7rem]/event:block",
              usesColorHex
                ? themeText
                : isSlot
                  ? "text-sky-950 dark:text-sky-100"
                  : newPatient
                    ? "text-violet-950 dark:text-violet-50"
                    : "text-emerald-950 dark:text-emerald-50",
            )}
          >
            <time
              dateTime={event.startAt.toISO()}
              className={cn(
                // Same size + leading as the patient-name line so, with both
                // columns top-anchored, the start time shares the name's exact
                // baseline (its bottom lines up with the name's bottom).
                "block text-[0.8125rem] font-bold leading-snug sm:text-sm",
                usesColorHex ? themeText : "text-foreground dark:text-white",
              )}
            >
              {eventStartLabel}
            </time>
            <time
              dateTime={event.endAt.toISO()}
              className={cn(
                // Same size + leading + top margin as the secondary info line, so
                // the end time shares that line's baseline (bottom-aligned with it).
                "mt-0.5 block text-[0.625rem] leading-snug sm:text-[0.6875rem]",
                usesColorHex
                  ? cn(themeText, "opacity-75")
                  : isSlot
                    ? "text-sky-700 dark:text-sky-300/55"
                    : newPatient
                      ? "text-violet-700 dark:text-violet-400/70"
                      : "text-emerald-800 dark:text-emerald-400/70",
              )}
            >
              {eventEndLabel}
            </time>
          </div>
        ) : null}
      </div>
    </ScrollArea>
  );

  const sharedClassName = cn(
    // Named container so the fixed-width time block (below) can hide itself once the
    // rendered card is too narrow to hold it — day-view lanes shrink to a fraction of the
    // column when appointments overlap, and "full" is used unconditionally there regardless
    // of how many lanes split the width.
    "@container/event group absolute top-0.5 bottom-1 overflow-hidden rounded-xl border text-left transition",
    // Slots: dashed border + ghost fill + flat (no shadow) — reads as "placeholder".
    // Bookings: solid border + filled tint + soft shadow + a 3px left accent rail in the
    // calendar's deep tone (via `before:` pseudo-element) — reads as "committed".
    isSlot
      ? "border-dashed"
      : "shadow-sm before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:rounded-l-[inherit] before:content-['']",
    usesColorHex
      ? isSlot
        ? themeCardSlot
        : newPatient
          ? cn(
              themeCardAppt,
              "border-dashed before:bg-(--event-dot-light) dark:before:bg-(--event-dot-dark)",
            )
          : cn(themeCardAppt, "before:bg-(--event-text-light) dark:before:bg-(--event-text-dark)")
      : isSlot
        ? "border-sky-300/70 bg-sky-50/40 hover:border-sky-400/80 hover:bg-sky-50/70 dark:border-sky-500/35 dark:bg-sky-950/30 dark:hover:border-sky-400/50 dark:hover:bg-sky-950/45"
        : newPatient
          ? "border-dashed border-violet-300/80 bg-violet-50/90 hover:border-violet-400 hover:bg-violet-100/90 dark:border-violet-500/40 dark:bg-violet-950/40 dark:hover:border-violet-400/55 dark:hover:bg-violet-950/55 before:bg-violet-500 dark:before:bg-violet-400"
          : "border-emerald-200 bg-emerald-50 hover:border-emerald-300 hover:bg-emerald-100/85 dark:border-emerald-700/35 dark:bg-[#0f2419] dark:hover:border-emerald-600/45 dark:hover:bg-[#112a1c] before:bg-emerald-500 dark:before:bg-emerald-400",
    Component === "button" && isSelected && "border-primary/40 ring-2 ring-primary/60 hover:ring-2 hover:ring-primary/60",
    Component === "button" && !isSelected && "hover:ring-1 hover:ring-foreground/10",
    // "Show this appointment on the calendar" (e.g. double-click in the Messages scheduling
    // panel): an amber outline, deliberately not a border or a ring — the app-wide unlayered
    // `* { @apply border-border }` outranks border utilities, and rings are box-shadow-based
    // so they'd collide with both the selected ring above and the arrival pulse. Amber also
    // stays distinct from the primary selection ring and the emerald drop-target ring.
    isHighlighted && "outline-2 outline-offset-1 outline-amber-500 dark:outline-amber-400 appointment-focus-pulse",
    // Drag-over: arm the slot — switch dashed→solid, lift, ring. Keeps the `+` icon visible
    // so it reads as a real drop target rather than just a highlight.
    isOver && "z-10 scale-[1.02] border-solid shadow-md ring-2 ring-emerald-400",
    isDragging && "cursor-grabbing border-dashed",
    className,
  );

  const cancelledBlock =
    cancelled ? (
      <>
        <div className="absolute inset-0 z-0 flex min-h-0 flex-col overflow-hidden rounded-[inherit] opacity-50">
          <CancelledStripeOverlay />
          {body}
        </div>
        <CancelledBanner reason={event.cancellationReason} innerRef={cancelledBannerRef} />
      </>
    ) : (
      body
    );

  // Lock-first-15-min appointments still render full height; the marker hatches
  // the bookable tail (from the 15-minute boundary to the real end).
  const partiallyFree = isAppointment && !cancelled && isAppointmentPartiallyFree(event);
  const freeMarker = partiallyFree ? (
    <FreeRemainderMarker
      topPercent={
        (LOCK_15M_MINUTES /
          Math.max(event.endAt.diff(event.startAt, "minutes").minutes, LOCK_15M_MINUTES)) *
        100
      }
    />
  ) : null;

  const handleEventClick = (e) => {
    e.stopPropagation();
    onClick?.(event.id);
  };

  // Double-click (and Enter/Space) opens the full editor for appointments;
  // slots open the booking modal. Recurring occurrences fall back to an
  // explanatory toast (the endpoint rejects per-occurrence edits).
  const openEventDetails = () => {
    onClick?.(event.id);
    if (isAppointment) {
      if (!mutations?.applyAppointmentUpdate) return; // editor context unavailable → no-op
      if (isRecurringOccurrenceId(event.id)) {
        toast.info("Recurring appointment", {
          description: "Occurrences of a recurring series can't be edited individually yet.",
        });
        return;
      }
      setEditorOpen(true);
      return;
    }
    setModalOpen(true); // slots → booking modal
  };

  const handleEventDoubleClick = (e) => {
    e.stopPropagation();
    openEventDetails();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.stopPropagation();
      e.preventDefault();
      openEventDetails();
    }
  };

  // --- Right-click context menu (appointments only) -------------------------
  const recurringOccurrence = isAppointment && isRecurringOccurrenceId(event.id);
  const canAmend = Boolean(mutations?.applyAppointmentUpdate) && !recurringOccurrence;
  const realDurationMinutes = isAppointment
    ? Math.round(event.endAt.diff(event.startAt, "minutes").minutes)
    : 0;
  // A real (non-NIL) patientId messages the linked user; a new-patient booking
  // (NIL id, no user record) messages by the phone on the appointment instead —
  // the backend find-or-creates a patient by that number.
  const chatPatientId = String(event.patientId || "").trim();
  const hasRealPatient = Boolean(chatPatientId) && chatPatientId !== NIL_PATIENT_ID;
  const chatPhone = newPatientPhoneFromEvent(event);
  const canGoToChat = isAppointment && (hasRealPatient || Boolean(chatPhone));
  const [goToChatBusy, setGoToChatBusy] = useState(false);
  const [copyConfirmationBusy, setCopyConfirmationBusy] = useState(false);
  // The in-flight guard has to be a ref, not the state above: one menu click can
  // deliver two selects, and both would read the same stale `false` before either
  // setState lands — firing the draft request twice. The state is only the label.
  const copyConfirmationBusyRef = useRef(false);

  const handleGoToChat = async () => {
    if (!canGoToChat || goToChatBusy) return;
    setGoToChatBusy(true);
    try {
      // Find-or-create the patient's thread, then jump to Messages — same flow
      // as the Patients page "Message" action.
      const { threadId } = await ensurePatientThread({
        patientUserId: hasRealPatient ? chatPatientId : undefined,
        displayName: String(event.patient || event.title || "").trim() || undefined,
        phone: chatPhone || undefined,
      });
      if (threadId) {
        requestOpenThreadMessage({ threadId });
      } else {
        toast.error("Could not open a conversation for this patient.");
      }
    } catch (error) {
      toast.error(error?.message || "Could not open the chat.");
    } finally {
      setGoToChatBusy(false);
    }
  };

  const handleToggleLock15 = () => {
    if (!canAmend) return;
    const next = !effectiveLock15m(event);
    mutations.applyAppointmentUpdate({
      event,
      changes: { lock15m: next },
      toastLabel: next
        ? "Only the first 15 minutes are locked — the rest is free for booking"
        : "Full appointment duration locked again",
    });
  };

  const handleMenuCancelOrRestore = () => {
    if (!canAmend) return;
    mutations.applyAppointmentUpdate({
      event,
      changes: { status: cancelled ? "scheduled" : "cancelled" },
      toastLabel: cancelled ? "Appointment restored" : "Appointment cancelled",
    });
  };

  // Confirmation actions. "Send confirmation…" raises the shared review-then-send
  // dialog (needs a real patient to message); "Mark confirmed" / "Clear" stamp
  // the state directly for phone/in-person confirmations. (isConfirmed is
  // computed above alongside confirmationStatus.)
  const handleSendConfirmation = () => {
    if (!canGoToChat) return;
    mutations?.requestAppointmentConfirmation?.({
      appointmentId: event.id,
      // NIL for a new patient — the confirmation dialog keys the draft and the
      // pending-mark on the zero-id appointment, and messages by phone.
      patientUserId: chatPatientId,
      patientName: String(event.patient || event.title || "").trim(),
      phone: chatPhone,
    });
  };

  // "Copy confirmation text" renders the same draft the send dialog would show and
  // puts it on the clipboard instead. Deliberately available where "Send
  // confirmation…" is not: it needs no chat thread, which is exactly the case where
  // staff have to paste the wording somewhere else (a phone call, another channel).
  const handleCopyConfirmationText = async () => {
    if (!canAmend || copyConfirmationBusyRef.current) return;
    copyConfirmationBusyRef.current = true;
    setCopyConfirmationBusy(true);
    try {
      const response = await fetchAppointmentConfirmationDraft({
        // The draft is keyed on the appointment; the path id may be the NIL
        // sentinel for a patient with no record yet.
        userId: chatPatientId || NIL_PATIENT_ID,
        appointmentId: event.id,
      });
      const text = String(response?.text || "").trim();
      if (!text) {
        toast.error("This appointment has no confirmation text.");
        return;
      }
      await copyTextToClipboard(text);
      toast.success("Confirmation text copied");
    } catch (error) {
      toast.error(error?.message || "Could not copy the confirmation text.");
    } finally {
      copyConfirmationBusyRef.current = false;
      setCopyConfirmationBusy(false);
    }
  };

  const setConfirmationState = async (status, toastLabel) => {
    if (!canAmend) return;
    try {
      await setAdminAppointmentConfirmation({
        userId: chatPatientId || NIL_PATIENT_ID,
        appointmentId: event.id,
        status,
        via: status === "confirmed" ? "manual" : undefined,
      });
      publishCalendarRefresh({ source: "appointment-confirmation", appointmentId: event.id });
      toast.success(toastLabel);
    } catch (error) {
      toast.error(error?.message || "Could not update confirmation.");
    }
  };

  const resizeHandleClass =
    "absolute inset-x-1 z-40 h-2 cursor-ns-resize touch-none opacity-0 transition-opacity group-hover:opacity-100 motion-reduce:transition-none";
  const resizeHandles = canResize ? (
    <>
      <span
        role="presentation"
        onPointerDown={beginResize("top")}
        onPointerMove={handleResizeMove}
        onPointerUp={finishResize}
        onPointerCancel={abortResize}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        className={cn(resizeHandleClass, "top-0")}
      >
        <span className="mx-auto mt-[3px] block h-1 w-8 rounded-full bg-foreground/25" />
      </span>
      <span
        role="presentation"
        onPointerDown={beginResize("bottom")}
        onPointerMove={handleResizeMove}
        onPointerUp={finishResize}
        onPointerCancel={abortResize}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        className={cn(resizeHandleClass, "bottom-0")}
      >
        <span className="mx-auto mt-[3px] block h-1 w-8 rounded-full bg-foreground/25" />
      </span>
      {resizePreview ? (
        <span
          className={cn(
            "pointer-events-none absolute left-1/2 z-50 -translate-x-1/2 rounded bg-foreground px-1.5 py-0.5 text-[0.625rem] font-semibold tabular-nums text-background shadow-md",
            resizePreview.edge === "top" ? "top-1" : "bottom-1",
          )}
        >
          {formatTime(resizePreview.edge === "top" ? resizePreview.startAt : resizePreview.endAt, {
            hour12: use12HourFormat,
          })}
        </span>
      ) : null}
    </>
  ) : null;

  const buttonOrDiv = Component === "button" ? (
    <button
      ref={setNodeRef}
      type="button"
      data-calendar-event-card=""
      onClick={handleEventClick}
      onDoubleClick={handleEventDoubleClick}
      style={sharedStyle}
      aria-pressed={isSelected}
      className={cn(sharedClassName, isAppointment && "touch-none")}
      {...attributes}
      {...listeners}
    >
      {cancelledBlock}
      {freeMarker}
      {resizeHandles}
    </button>
  ) : (
    <div
      ref={setNodeRef}
      role="button"
      tabIndex={0}
      data-calendar-event-card=""
      onClick={handleEventClick}
      onDoubleClick={handleEventDoubleClick}
      onKeyDown={handleKeyDown}
      style={sharedStyle}
      className={cn("cursor-pointer", sharedClassName, isAppointment && "touch-none")}
      {...attributes}
      {...listeners}
    >
      {cancelledBlock}
      {freeMarker}
      {resizeHandles}
    </div>
  );

  // Right-click on an appointment card opens the actions menu; slots keep
  // their default behavior (their whole card is already a booking target).
  const cardWithMenu = isAppointment ? (
    <ContextMenu>
      <ContextMenuTrigger asChild>{buttonOrDiv}</ContextMenuTrigger>
      <ContextMenuContent className="w-60">
        <ContextMenuItem onSelect={() => openEventDetails()}>
          <CalendarClock className="mr-2 size-4 text-muted-foreground" aria-hidden />
          Reschedule / edit…
        </ContextMenuItem>
        <ContextMenuItem
          disabled={!canGoToChat || goToChatBusy}
          onSelect={() => handleGoToChat()}
        >
          <MessageCircle className="mr-2 size-4 text-muted-foreground" aria-hidden />
          Go to chat
        </ContextMenuItem>
        {directEntry ? null : (
          <>
            <ContextMenuItem disabled={!canGoToChat} onSelect={() => handleSendConfirmation()}>
              <Send className="mr-2 size-4 text-muted-foreground" aria-hidden />
              Send confirmation…
            </ContextMenuItem>
            <ContextMenuItem
              disabled={!canAmend || copyConfirmationBusy}
              onSelect={() => handleCopyConfirmationText()}
            >
              <ClipboardCopy className="mr-2 size-4 text-muted-foreground" aria-hidden />
              {copyConfirmationBusy ? "Copying…" : "Copy confirmation text"}
            </ContextMenuItem>
            {isConfirmed ? (
              <ContextMenuItem
                disabled={!canAmend}
                onSelect={() => setConfirmationState("", "Confirmation cleared")}
              >
                <CircleSlash className="mr-2 size-4 text-muted-foreground" aria-hidden />
                Clear confirmation
              </ContextMenuItem>
            ) : (
              <ContextMenuItem
                disabled={!canAmend}
                onSelect={() => setConfirmationState("confirmed", "Marked as confirmed")}
              >
                <CalendarCheck className="mr-2 size-4 text-muted-foreground" aria-hidden />
                Mark confirmed
              </ContextMenuItem>
            )}
          </>
        )}
        {realDurationMinutes > 15 ? (
          <ContextMenuCheckboxItem
            checked={effectiveLock15m(event)}
            disabled={!canAmend || cancelled}
            onCheckedChange={handleToggleLock15}
          >
            Lock first 15 min only (free the rest)
          </ContextMenuCheckboxItem>
        ) : null}
        <ContextMenuSeparator />
        {cancelled ? (
          <ContextMenuItem disabled={!canAmend} onSelect={handleMenuCancelOrRestore}>
            <RotateCcw className="mr-2 size-4 text-muted-foreground" aria-hidden />
            Restore appointment
          </ContextMenuItem>
        ) : (
          <ContextMenuItem
            disabled={!canAmend}
            onSelect={handleMenuCancelOrRestore}
            className="text-destructive focus:text-destructive data-highlighted:text-destructive"
          >
            <CalendarX2 className="mr-2 size-4" aria-hidden />
            {directEntry ? "Cancel entry…" : "Cancel appointment…"}
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  ) : (
    buttonOrDiv
  );

  return (
    <>
      {cardWithMenu}
      {isSlot ? (
        <SlotBookingModal
          event={event}
          open={modalOpen}
          onOpenChange={setModalOpen}
          use12HourFormat={use12HourFormat}
          onEditSlot={() => setSlotEditOpen(true)}
        />
      ) : null}
      {isSlot ? (
        <SlotFormSheet
          open={slotEditOpen}
          onOpenChange={setSlotEditOpen}
          mode="edit"
          slot={event}
          selectedDate={event.startAt}
        />
      ) : null}
      {isAppointment ? (
        <AppointmentFormSheet
          open={editorOpen}
          onOpenChange={setEditorOpen}
          event={event}
          use12HourFormat={use12HourFormat}
        />
      ) : null}
    </>
  );
}

export default CalendarEventItem;
